

import React from "react";
import { useParams, Link } from "react-router-dom";

/**
 * Handout page (web view)
 * - Fetches talk data by :id
 * - Renders a clean on-screen layout
 * - Provides a "Download PDF" button that routes to /handout/:id/pdf
 */
export default function HandoutView() {
    const { id: routeId, talkId: routeTalkId } = useParams();
    // Support either /handout/:id or /handout/:talkId based on router setup
    const id = routeId || routeTalkId;

    const [state, setState] = React.useState({ loading: true, error: null, talk: null });

    React.useEffect(() => {
        let isMounted = true;

        async function load() {
            setState((s) => ({ ...s, loading: true, error: null }));
            try {
                const base = process.env.REACT_APP_API_BASE || ""; // optional override
                const url = `${base.replace(/\/$/, "")}/api/talks/${encodeURIComponent(id)}`;
                const res = await fetch(url, { headers: { Accept: "application/json" } });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const talk = await res.json();
                if (!isMounted) return;
                setState({ loading: false, error: null, talk });
            } catch (err) {
                if (!isMounted) return;
                setState({ loading: false, error: err?.message || "Failed to load", talk: null });
            }
        }

        if (id) load();
        return () => {
            isMounted = false;
        };
    }, [id]);

    const { loading, error, talk } = state;

    if (!id) {
        return (
            <div className="container" style={{ padding: "2rem" }}>
                <h2>Missing talk id</h2>
                <p>Please provide a talk id in the URL.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container" style={{ padding: "2rem" }}>
                <h2>Loading handout…</h2>
                <p>Please wait.</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container" style={{ padding: "2rem" }}>
                <h2>Couldn’t load handout</h2>
                <p style={{ color: "#b00020" }}>{String(error)}</p>
            </div>
        );
    }

    // Defensive helpers for unknown shapes
    const sections = Array.isArray(talk?.sections) ? talk.sections : [];
    const subtitleBits = [talk?.series, talk?.speaker].filter(Boolean);

    return (
        <div className="container handout" style={{ maxWidth: 900, padding: "2rem 1.25rem" }}>
            {/* Brand / heading */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <div>
                    <h1 style={{ margin: 0 }}>{talk?.title || "Handout"}</h1>
                    {subtitleBits.length > 0 && (
                        <div style={{ opacity: 0.8, marginTop: 4 }}>{subtitleBits.join(" • ")}</div>
                    )}
                </div>
                <div>
                    {/* Download button navigates to the PDF route we added */}
                    <Link className="btn btn-primary" to={`/handout/${encodeURIComponent(id)}/pdf`}>
                        Download PDF
                    </Link>
                </div>
            </div>

            {/* Meta line (date, location, etc.) */}
            {(talk?.date || talk?.location) && (
                <div style={{ marginTop: 8, opacity: 0.75 }}>
                    {[talk?.date, talk?.location].filter(Boolean).join(" • ")}
                </div>
            )}

            {/* Divider */}
            <hr style={{ margin: "1.25rem 0" }} />

            {/* Sections */}
            <div style={{ display: "grid", gap: 20 }}>
                {sections.map((section, idx) => (
                    <SectionBlock key={idx} section={section} />
                ))}
            </div>

            {/* Notes area (optional) */}
            {Array.isArray(talk?.notes) && talk.notes.length > 0 && (
                <div style={{ marginTop: 28 }}>
                    <h3 style={{ marginBottom: 8 }}>Notes</h3>
                    <ul style={{ marginTop: 0 }}>
                        {talk.notes.map((n, i) => (
                            <li key={i}>{n}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function SectionBlock({ section }) {
    if (!section) return null;
    const title = section.title || section.heading || section.label;
    const points = Array.isArray(section.points)
        ? section.points
        : Array.isArray(section.items)
            ? section.items
            : [];
    const body = section.body || section.content || section.description || "";
    const isDivider = section.type === "divider" || section.kind === "divider" || section.divider === true;

    if (isDivider) {
        return (
            <div className="handout-divider" style={{ margin: "16px 0", textAlign: "center" }}>
                <div style={{ fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", opacity: 0.8 }}>{title || "Section"}</div>
            </div>
        );
    }

    return (
        <section>
            {title && <h3 style={{ margin: "0 0 6px" }}>{title}</h3>}
            {body && <p style={{ marginTop: 0 }}>{body}</p>}
            {points.length > 0 && (
                <ul style={{ marginTop: body ? 8 : 0 }}>
                    {points.map((p, i) => (
                        <li key={i}>{renderMaybeRich(p)}</li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function renderMaybeRich(node) {
    // Supports either string or { text } or richer objects
    if (typeof node === "string") return node;
    if (node && typeof node === "object") {
        return node.text || node.value || JSON.stringify(node);
    }
    return String(node);
}